import { useParams, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getFormDefinition, type FormType } from '../../../../shared/form-types';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

// Form components - Grupo A: Simple/Departmental forms
import { AccessForm } from './forms/AccessForm';
import { DepartmentalRequestForm } from './forms/DepartmentalRequestForm';

// Form components - Grupo B: Hardware
import { HardwareBugForm } from './forms/HardwareBugForm';
import { HardwareRequestForm } from './forms/HardwareRequestForm';
import { HardwareSaleForm } from './forms/HardwareSaleForm';

// Form components - Grupo E: User Management
import { UserManagementForm } from './forms/UserManagementForm';
import { UserOnboardingForm } from './forms/UserOnboardingForm';
import { UserOffboardingForm } from './forms/UserOffboardingForm';

const FORM_COMPONENTS: Record<string, React.ComponentType> = {
  // Grupo A - Departmental (8 forms) ✅
  'access': AccessForm,
  'content': DepartmentalRequestForm,
  'finance': DepartmentalRequestForm,
  'marketing': DepartmentalRequestForm,
  'owned-media': DepartmentalRequestForm,
  'people': DepartmentalRequestForm,
  'sales': DepartmentalRequestForm,
  'strategy': DepartmentalRequestForm,

  // Grupo B - Hardware (3 forms) ✅
  'hardware-bug': HardwareBugForm,
  'hardware-request': HardwareRequestForm,
  'hardware-sale': HardwareSaleForm,

  // Grupo E - User Management (3 forms) ✅
  'user-management': UserManagementForm,
  'user-onboarding': UserOnboardingForm,
  'user-offboarding': UserOffboardingForm,
};

export function NewTicketPage() {
  const { t } = useTranslation();
  const { formType } = useParams<{ formType: string }>();

  // Validate form type
  if (!formType || !getFormDefinition(formType as FormType)) {
    return <Navigate to="/tickets/select" replace />;
  }

  const FormComponent = FORM_COMPONENTS[formType];

  // If form component not yet implemented, show placeholder
  if (!FormComponent) {
    return (
      <div className="max-w-4xl">
        <div className="mb-6">
          <Link to="/tickets/select">
            <Button variant="ghost" size="sm" className="mb-4">
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('tickets.new_ticket_page.back')}
            </Button>
          </Link>
          <h2 className="text-3xl font-bold tracking-tight">{formType ? t(`tickets.forms.${formType}.name`) : ''}</h2>
          <p className="text-muted-foreground">{formType ? t(`tickets.forms.${formType}.description`) : ''}</p>
        </div>

        <div className="rounded-lg border border-dashed p-12 text-center">
          <h3 className="text-lg font-semibold mb-2">{t('tickets.new_ticket_page.in_development')}</h3>
          <p className="text-muted-foreground mb-4">
            {t('tickets.new_ticket_page.in_development_msg')}
          </p>
          <Link to="/tickets/select">
            <Button>{t('tickets.new_ticket_page.back_to_selection')}</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      <div className="mb-6">
        <Link to="/tickets/select">
          <Button variant="ghost" size="sm" className="mb-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('tickets.new_ticket_page.back')}
          </Button>
        </Link>
        <h2 className="text-3xl font-bold tracking-tight">{formType ? t(`tickets.forms.${formType}.name`) : ''}</h2>
      </div>

      <FormComponent />
    </div>
  );
}
